import React, { useEffect, useState } from "react";
import {
  Divider,
  Table,
  Card,
  Tag,
  Space,
  Row,
  Col,
  Grid,
  Collapse,
} from "antd";
import { ClockCircleOutlined, ShoppingOutlined } from "@ant-design/icons";
import moment from "moment";
import axiosWithAuth from "../../../utils/axiosWithAuth";
import { TABLE_DATE_FORMAT } from "../../../common/constants";

import OrderTaxRows from "../../../common/OrderTaxRows";
import OrderTipRow from "../../../common/OrderTipRow";

import useOrderTaxSummary from "../../../hooks/useOrderTaxSummary";

export default function OrderInfoUser(props) {
  const { order_id } = props;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { Panel } = Collapse;

  const [orderDetails, setOrderDetails] = useState();

  const dateFormat = TABLE_DATE_FORMAT;
  const taxSummary = useOrderTaxSummary(orderDetails);

  const columns = [
    {
      title: "Dish Name",
      key: "dish_name",
      render: (text) => (
        <span style={{ fontWeight: "500", color: "#262626" }}>
          {text.dish_name}
        </span>
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      align: "center",
      width: 120,
      render: (text) => (
        <span style={{ color: "#595959" }}>x{text.dish_quantity}</span>
      ),
    },
    {
      title: "Price",
      key: "total",
      align: "right",
      width: 120,
      render: (text) => (
        <span style={{ fontWeight: "500", color: "#262626" }}>
          ${(text.dish_price * text.dish_quantity).toFixed(2)}
        </span>
      ),
    },
  ];

  useEffect(() => {
    getOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id]);

  const getOrderDetails = async () => {
    try {
      const response = await axiosWithAuth().get(
        `/api/restaurant/order/getOrdersDetails/${order_id}`,
      );

      console.log("RESPONSE", response.data.data.order);

      setOrderDetails(response.data.data.order);
    } catch (error) {
      console.error("Error getting orders:", error);
    }
  };

  const getOrderStatus = (status) => {
    const statusConfig = {
      // pending: { color: "orange", text: "Pending Payment" },
      processing: { color: "blue", text: "Processing" },
      shipped: { color: "cyan", text: "Shipped" },
      completed: { color: "green", text: "Completed" },
      canceled: { color: "red", text: "Canceled" },
    };
    return statusConfig[status] || { color: "default", text: status };
  };

  return (
    <>
      {orderDetails && (
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          {/* Order Header */}
          <Card
            style={{
              marginBottom: "20px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={12}>
                <Space direction="vertical" size={4}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#262626",
                    }}
                  >
                    Order #{order_id}
                  </div>
                  <Space size={8}>
                    <ClockCircleOutlined style={{ color: "#8c8c8c" }} />
                    <span style={{ color: "#8c8c8c", fontSize: "14px" }}>
                      {moment(new Date(orderDetails.date_order)).format(
                        dateFormat,
                      )}
                    </span>
                  </Space>
                </Space>
              </Col>
              <Col xs={24} md={12} style={{ textAlign: "right" }}>
                <Tag
                  color={getOrderStatus(orderDetails.order_status).color}
                  style={{
                    fontSize: "14px",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontWeight: "500",
                  }}
                >
                  {getOrderStatus(orderDetails.order_status).text}
                </Tag>
              </Col>
            </Row>
          </Card>

          {/* Order Items */}
          <Card
            title={
              <Space>
                <ShoppingOutlined />
                <span>Order Items</span>
              </Space>
            }
            style={{
              marginBottom: "20px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            {screens.lg ? (
              <Table
                columns={columns}
                dataSource={orderDetails.detail}
                pagination={false}
                style={{ marginBottom: "20px" }}
              />
            ) : (
              <Collapse accordion style={{ marginBottom: "20px" }}>
                {orderDetails.detail.map((item, index) => (
                  <Panel
                    key={index}
                    header={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <strong>{item.dish_name}</strong>
                        </span>
                        <span style={{ fontSize: "14px", color: "#666" }}>
                          ${(item.dish_price * item.dish_quantity).toFixed(2)}
                        </span>
                      </div>
                    }
                  >
                    <p>
                      <strong>Dish:</strong> {item.dish_name}
                    </p>
                    <p>
                      <strong>Price:</strong> ${item.dish_price.toFixed(2)}
                    </p>
                    <p>
                      <strong>Quantity:</strong> x{item.dish_quantity}
                    </p>
                    <p>
                      <strong>Total:</strong> $
                      {(item.dish_price * item.dish_quantity).toFixed(2)}
                    </p>
                  </Panel>
                ))}
              </Collapse>
            )}

            {/* Price Breakdown */}
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "20px",
                borderRadius: "8px",
              }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between">
                  <Col style={{ color: "#595959", fontSize: "14px" }}>
                    Subtotal
                  </Col>
                  <Col style={{ fontSize: "14px" }}>
                    ${taxSummary.subtotal?.toFixed(2)}
                  </Col>
                </Row>
                <Row justify="space-between">
                  <Col style={{ color: "#595959", fontSize: "14px" }}>
                    Delivery Fee
                  </Col>
                  <Col style={{ fontSize: "14px" }}>
                    ${parseFloat(orderDetails.delivery_price).toFixed(2)}
                  </Col>
                </Row>
                <OrderTaxRows summary={taxSummary} />
                <OrderTipRow tip={taxSummary.tip} />
                <Divider style={{ margin: "8px 0" }} />
                <Row justify="space-between">
                  <Col
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#262626",
                    }}
                  >
                    Total
                  </Col>
                  <Col
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#1890ff",
                    }}
                  >
                    ${parseFloat(orderDetails.total_price_order).toFixed(2)}
                  </Col>
                </Row>
              </Space>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
